import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Auth store. The persisted localStorage shape is the cross-hub `solid-auth`
 * contract the app rail reads natively — EXACTLY:
 *   { state: { accessToken, refreshToken, user, isAuthenticated }, version: 0 }
 * (activeOrgId is intentionally NOT part of that contract; it is persisted
 * separately under `financials-active-org`.)
 *
 * After every token write we dispatch `solid-auth-updated` so the app rail
 * refreshes; `app-rail:logout` clears the store (wired in AuthProvider-land
 * below).
 */

export interface AuthOrg {
  id: string;
  name: string;
  slug: string;
  role: string;
}

export interface AuthUser {
  sub?: string;
  email: string;
  name: string;
  /**
   * ⚠ THE FULL CLAIM OBJECTS, NOT A NARROWED SHAPE. The persisted `solid-auth`
   * store is a CROSS-HUB contract, and the app rail renders its switcher from
   * `user.apps` — expecting auth's own claim fields (`client_id`, `name`,
   * `url`, `iconName`, `color`). This app once narrowed each entry to
   * `{clientId}` and threw the rest away at sign-in, which is why the rail on
   * financials showed identity and sign-out but ZERO apps: it received a list
   * of objects with none of the fields it draws. `clientId` is OUR normalised
   * key; everything else passes through untouched for the rail.
   */
  apps?: Array<{ clientId: string; [claim: string]: unknown }>;
  orgs?: AuthOrg[];
}

const ACTIVE_ORG_KEY = 'financials-active-org';

function readStoredActiveOrg(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_ORG_KEY);
  } catch {
    return null;
  }
}

function writeStoredActiveOrg(orgId: string | null): void {
  try {
    if (orgId === null) window.localStorage.removeItem(ACTIVE_ORG_KEY);
    else window.localStorage.setItem(ACTIVE_ORG_KEY, orgId);
  } catch {
    /* private mode etc. — selection just won't survive reload */
  }
}

function notifyAuthUpdated(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('solid-auth-updated'));
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** Active organization id — sent as X-Organization on every API call. */
  activeOrgId: string | null;
  setAuth: (auth: { accessToken: string; refreshToken?: string | null; user: AuthUser }) => void;
  /** Token rotation (silent refresh). Keeps the newest refresh token. */
  setTokens: (tokens: { accessToken: string; refreshToken?: string | null }) => void;
  /**
   * Replace the user WITHOUT touching tokens — used to re-derive claims for a
   * session that signed in before a claims-shape change. Re-picks the active
   * org, because the previously stored one may not exist in the new list (and,
   * in the case this was written for, there was no list at all).
   */
  setUser: (user: AuthUser) => void;
  setActiveOrg: (orgId: string) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      activeOrgId: typeof window !== 'undefined' ? readStoredActiveOrg() : null,
      setAuth: ({ accessToken, refreshToken = null, user }) =>
        set((prev) => {
          const orgs = user.orgs ?? [];
          const activeOrgId =
            prev.activeOrgId && orgs.some((o) => o.id === prev.activeOrgId)
              ? prev.activeOrgId
              : (orgs[0]?.id ?? null);
          writeStoredActiveOrg(activeOrgId);
          queueMicrotask(notifyAuthUpdated);
          return { accessToken, refreshToken, user, isAuthenticated: true, activeOrgId };
        }),
      setTokens: ({ accessToken, refreshToken }) =>
        set((prev) => {
          queueMicrotask(notifyAuthUpdated);
          // Always keep the newest refresh token; fall back to the previous one
          // if the IdP did not rotate it.
          return { accessToken, refreshToken: refreshToken ?? prev.refreshToken };
        }),
      setUser: (user) =>
        set((prev) => {
          const orgs = user.orgs ?? [];
          const activeOrgId =
            prev.activeOrgId && orgs.some((o) => o.id === prev.activeOrgId)
              ? prev.activeOrgId
              : (orgs[0]?.id ?? null);
          writeStoredActiveOrg(activeOrgId);
          queueMicrotask(notifyAuthUpdated);
          return { user, activeOrgId };
        }),
      setActiveOrg: (orgId) => {
        writeStoredActiveOrg(orgId);
        set({ activeOrgId: orgId });
      },
      clear: () => {
        writeStoredActiveOrg(null);
        queueMicrotask(notifyAuthUpdated);
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          activeOrgId: null,
        });
      },
    }),
    {
      name: 'solid-auth',
      version: 0,
      // Exact cross-hub persisted shape — nothing extra.
      partialize: (s) => ({
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        user: s.user,
        isAuthenticated: s.isAuthenticated,
      }),
    },
  ),
);

/** The resolved active org (falls back to the user's first org). */
export function selectActiveOrg(s: Pick<AuthState, 'user' | 'activeOrgId'>): AuthOrg | undefined {
  const orgs = s.user?.orgs ?? [];
  return orgs.find((o) => o.id === s.activeOrgId) ?? orgs[0];
}

// Cross-app conventions: the hub app rail dispatches `app-rail:logout` when the
// user signs out from the rail — clear our store (the rail handles IdP logout).
if (typeof window !== 'undefined') {
  window.addEventListener('app-rail:logout', () => {
    useAuthStore.getState().clear();
  });
}
